import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import {
  DataSourceSettingsDto,
  DataSourceSettingsMutatePayload,
  MutationOptions,
  MutationResult,
  QueryResult,
} from '@thingslog/repositories';
import { QueryKeys } from '../enums/QueryKeys';
import { QueryOptions } from '@thingslog/repositories';
import { buildDashboardUrl } from './dashboardUrlUtils';

export function createDashboardDataSourceQueryClient(
  axios: AxiosInstance
): DashboardDataSourceQueryClient {
  return new DashboardDataSourceQueryClientImp(axios);
}

class DashboardDataSourceQueryClientImp {
  public constructor(private axios: AxiosInstance) {}

  public useDataSources = (
    options?: QueryOptions<DataSourceSettingsDto[]>
  ): QueryResult<DataSourceSettingsDto[]> => {
    return useQuery<DataSourceSettingsDto[], AxiosError>(
      [QueryKeys.GetDataSources],
      () => this.getDataSources(),
      options
    );
  };

  public useDataSource = (
    id: string,
    options?: QueryOptions<DataSourceSettingsDto>
  ): QueryResult<DataSourceSettingsDto> => {
    return useQuery<DataSourceSettingsDto, AxiosError>(
      [QueryKeys.GetDataSource, id],
      () => this.getDataSource(id),
      options
    );
  };

  public useCreateDataSource = (
    options?: MutationOptions<DataSourceSettingsDto, DataSourceSettingsMutatePayload>
  ): MutationResult<DataSourceSettingsDto, DataSourceSettingsMutatePayload> => {
    return useMutation<DataSourceSettingsDto, AxiosError, DataSourceSettingsMutatePayload>(
      [QueryKeys.CreateDataSource],
      (body: DataSourceSettingsMutatePayload) => this.createDataSource(body),
      options
    );
  };

  public useUpdateDataSource = (
    options?: MutationOptions<
      DataSourceSettingsDto,
      { id: string; body: DataSourceSettingsMutatePayload }
    >
  ): MutationResult<
    DataSourceSettingsDto,
    { id: string; body: DataSourceSettingsMutatePayload }
  > => {
    return useMutation<
      DataSourceSettingsDto,
      AxiosError,
      { id: string; body: DataSourceSettingsMutatePayload }
    >(
      [QueryKeys.UpdateDataSource],
      ({ id, body }: { id: string; body: DataSourceSettingsMutatePayload }) =>
        this.updateDataSource(id, body),
      options
    );
  };

  public useDeleteDataSource = (
    options?: MutationOptions<boolean, string>
  ): MutationResult<boolean, string> => {
    return useMutation<boolean, AxiosError, string>(
      [QueryKeys.DeleteDataSource],
      (id: string) => this.deleteDataSource(id),
      options
    );
  };

  private getDataSources = async (): Promise<DataSourceSettingsDto[]> => {
    return await this.axios
      .get(buildDashboardUrl('/dashboard/api/dashboards/data-sources'))
      .then((response: AxiosResponse<DataSourceSettingsDto[]>) => response.data);
  };

  private getDataSource = async (id: string): Promise<DataSourceSettingsDto> => {
    return await this.axios
      .get(buildDashboardUrl(`/dashboard/api/dashboards/data-sources/${id}`))
      .then((response: AxiosResponse<DataSourceSettingsDto>) => response.data);
  };

  private createDataSource = async (
    body: DataSourceSettingsMutatePayload
  ): Promise<DataSourceSettingsDto> => {
    return await this.axios
      .post(buildDashboardUrl('/dashboard/api/dashboards/data-sources'), body)
      .then((response: AxiosResponse<DataSourceSettingsDto>) => response.data);
  };

  private updateDataSource = async (
    id: string,
    body: DataSourceSettingsMutatePayload
  ): Promise<DataSourceSettingsDto> => {
    return await this.axios
      .put(buildDashboardUrl(`/dashboard/api/dashboards/data-sources/${id}`), body)
      .then((response: AxiosResponse<DataSourceSettingsDto>) => response.data);
  };

  private deleteDataSource = async (id: string): Promise<boolean> => {
    return await this.axios
      .delete(buildDashboardUrl(`/dashboard/api/dashboards/data-sources/${id}`))
      .then((response: AxiosResponse<boolean>) => response.data);
  };
}

export interface DashboardDataSourceQueryClient {
  useDataSources: (
    options?: QueryOptions<DataSourceSettingsDto[]>
  ) => QueryResult<DataSourceSettingsDto[]>;
  useDataSource: (
    id: string,
    options?: QueryOptions<DataSourceSettingsDto>
  ) => QueryResult<DataSourceSettingsDto>;
  useCreateDataSource: (
    options?: MutationOptions<DataSourceSettingsDto, DataSourceSettingsMutatePayload>
  ) => MutationResult<DataSourceSettingsDto, DataSourceSettingsMutatePayload>;
  useUpdateDataSource: (
    options?: MutationOptions<
      DataSourceSettingsDto,
      { id: string; body: DataSourceSettingsMutatePayload }
    >
  ) => MutationResult<DataSourceSettingsDto, { id: string; body: DataSourceSettingsMutatePayload }>;
  useDeleteDataSource: (
    options?: MutationOptions<boolean, string>
  ) => MutationResult<boolean, string>;
}
